import { Sites } from '@ps/platform-message-framework/common/Sites';
import type { Currency } from '@ps/web-core-enums';
import { canUseDOM } from '@ps/web-core-utils';
import { isString } from 'lodash';

import { request } from '@swf/core/api/request';
import { getState, select } from '@swf/core/store';

import type {
  GooglePayServerSettings,
  PaySystemDetailsResponse,
  PaySystemInfo
} from '../types/Cashier';
import { CardType } from '../types/cardType';

const initGooglePayNativeClient = async () => {
  if (canUseDOM && !window.starsweb?.googlePay && window.starsweb?.module) {
    window.starsweb.module('googlePay');
  }
};

export const composeBaseConfigurationData = (paySystemInfo: PaySystemInfo, site: Sites) => {
  const {
    googlePayMerchantPublicKey,
    googleApiVersion,
    googleAllowedCardSchemes,
    googleAllowedAuthMethods,
    merchantCapabilities,
    googlePKIProtocolVersion,
    googlePayZeroExponentCurrencies,
    googleMerchantName,
    googleAcquisitionCountry
  } = paySystemInfo;

  const googlePaySettings = {
    merchantPublicKey: googlePayMerchantPublicKey,
    apiVersion: getApiVersion(googleApiVersion),
    allowedCardSchemes: googleAllowedCardSchemes || ['VISA', 'MASTERCARD'],
    allowedAuthMethods: googleAllowedAuthMethods || 'CRYPTOGRAM_3DS',
    allowedCardType: merchantCapabilities || ['allowPrepaidCards', 'allowCreditCards'],
    PKIProtocolVersion: googlePKIProtocolVersion || 'ECv2',
    zeroExponentCurrencies: googlePayZeroExponentCurrencies,
    merchantId: googleMerchantName,
    merchantName: 'Stars',
    allowedPaymentMethodType: 'CARD',
    countryCode: googleAcquisitionCountry || getCountryCode(site),
    googlePayApiVersion: getApiVersion(googleApiVersion)
  };
  return googlePaySettings;
};

export const getGooglePayBaseConfigurationData = (googlePaySettings: GooglePayServerSettings) => ({
  apiVersion: googlePaySettings.googlePayApiVersion.version,
  apiVersionMinor: googlePaySettings.googlePayApiVersion.minor,
  allowedPaymentMethods: [
    {
      type: googlePaySettings.allowedPaymentMethodType as google.payments.api.PaymentMethodType,
      parameters: {
        allowedAuthMethods: getAllowedAuthMethods(
          googlePaySettings.allowedAuthMethods
        ) as google.payments.api.CardAuthMethod[],
        allowedCardNetworks: getAllowedCardSchemes(
          googlePaySettings.allowedCardSchemes
        ) as google.payments.api.CardNetwork[],
        allowPrepaidCards: getAllowPrepaidCards(googlePaySettings.allowedCardType),
        allowCreditCards: getAllowCreditCards(googlePaySettings.allowedCardType),
        billingAddressRequired: false
      }
    }
  ]
});

export const getApiVersion = (googleApiVersion?: string): { version: number; minor: number } => {
  const versionStr = googleApiVersion;
  const apiVersion = { version: 2, minor: 0 }; // default
  if (versionStr) {
    const versions = versionStr.split('.');
    if (versions.length >= 2) {
      apiVersion.version = Number(versions[0]);
      apiVersion.minor = Number(versions[1]);
    }
  }
  return apiVersion;
};

export const getCountryCode = (site: Sites) => {
  if (
    [
      Sites.COM,
      Sites.ITA,
      Sites.EST,
      Sites.BEL,
      Sites.SPA,
      Sites.DE,
      Sites.DE_SH,
      Sites.EU,
      Sites.DEN,
      Sites.GR,
      Sites.BUL,
      Sites.ROM,
      Sites.CZE,
      Sites.SWE,
      Sites.UK
    ].includes(site)
  ) {
    return 'GB';
  } else if ([Sites.FRA, Sites.POR].includes(site)) {
    return 'NL';
  }
  return '';
};

export const getAllowedAuthMethods = (allowedAuthMethods: string | string[]): string[] =>
  isString(allowedAuthMethods) ? allowedAuthMethods.split(',') : (allowedAuthMethods as string[]);

export const getAllowedCardSchemes = (allowedCardSchemes: string | string[]): string[] => {
  const allowedCardSchemesOutput: string[] = isString(allowedCardSchemes)
    ? allowedCardSchemes.split(',')
    : (allowedCardSchemes as string[]);

  return allowedCardSchemesOutput.map((item) => item.toUpperCase());
};

export const getAllowPrepaidCards = (allowedCardType: string[]): boolean => {
  const allowedCardTypeTemp = allowedCardType.map((i) => i.toLowerCase());
  return (
    allowedCardTypeTemp.indexOf('allowprepaidcards') > -1 ||
    allowedCardTypeTemp.indexOf('capabilityprepaid') > -1
  );
};

export const getAllowCreditCards = (allowedCardType: string[]): boolean => {
  const allowedCardTypeTemp = allowedCardType.map((i) => i.toLowerCase());
  return (
    allowedCardTypeTemp.indexOf('allowcreditcards') > -1 ||
    allowedCardTypeTemp.indexOf('capabilitycredit') > -1
  );
};

export const googlePayCheckAvailability = async (
  data: google.payments.api.IsReadyToPayRequest,
  merchantPublicKey?: string
): Promise<boolean> => {
  const starsWebGooglePay = canUseDOM && window.starsweb?.googlePay;

  if (!starsWebGooglePay) return false;

  starsWebGooglePay.checkAvailability(data);

  const waitForEventWithTimeout = (): Promise<boolean> =>
    new Promise((resolve) => {
      const timer = setTimeout(() => {
        document.removeEventListener('googlePayUpdated', listener);
        document.removeEventListener('starsgooglePayUpdated', listener);
        resolve(false);
      }, 15000);

      const listener = () => {
        const { availableForPayment } = starsWebGooglePay;
        clearTimeout(timer);
        document.removeEventListener('googlePayUpdated', listener);
        document.removeEventListener('starsgooglePayUpdated', listener);
        resolve(availableForPayment);
      };

      document.addEventListener('googlePayUpdated', listener);
      document.addEventListener('starsgooglePayUpdated', listener);
    });

  const isAvailableForPayment = await waitForEventWithTimeout();

  return starsWebGooglePay.supported && !!merchantPublicKey && isAvailableForPayment;
};

export const getGooglePayNativeData = (
  currency: Currency,
  amount: number,
  googlePaySystemDetails: PaySystemInfo,
  site: Sites
): google.payments.api.PaymentDataRequest => {
  const googlePaySettings = composeBaseConfigurationData(googlePaySystemDetails, site);
  const paymentData = {
    apiVersion: googlePaySettings.googlePayApiVersion.version,
    apiVersionMinor: googlePaySettings.googlePayApiVersion.minor,
    allowedPaymentMethods: [
      {
        type: googlePaySettings.allowedPaymentMethodType as google.payments.api.PaymentMethodType,
        parameters: {
          allowedAuthMethods: getAllowedAuthMethods(
            googlePaySettings.allowedAuthMethods
          ) as google.payments.api.CardAuthMethod[],
          allowedCardNetworks: getAllowedCardSchemes(
            googlePaySettings.allowedCardSchemes
          ) as google.payments.api.CardNetwork[],
          allowPrepaidCards: getAllowPrepaidCards(googlePaySettings.allowedCardType),
          allowCreditCards: getAllowCreditCards(googlePaySettings.allowedCardType),
          billingAddressRequired: false
        },
        tokenizationSpecification: {
          type: 'DIRECT',
          parameters: {
            protocolVersion: googlePaySettings.PKIProtocolVersion,
            publicKey: googlePaySettings.merchantPublicKey
          }
        } as google.payments.api.DirectTokenizationSpecification
      }
    ],
    transactionInfo: {
      totalPrice: `${amount}`,
      totalPriceStatus: 'FINAL' as google.payments.api.TotalPriceStatus,
      countryCode: googlePaySettings.countryCode,
      currencyCode: currency
    },
    merchantInfo: {
      merchantName: googlePaySettings.merchantName,
      merchantId: googlePaySettings.merchantName
    }
  };
  return paymentData;
};

const getGooglePaySystemDetails = async (site: Sites, currency?: Currency) => {
  try {
    const {
      body: {
        paySystemDetails: { info }
      }
    } = await request<PaySystemDetailsResponse>({
      ...select.cms.getRequestOptions(getState()),
      service: 'wc3.paysystem.get.paySystemDetails',
      body: { currency, type: CardType.GooglePay }
    });

    return composeBaseConfigurationData(info, site);
  } catch {
    return undefined;
  }
};

export const checkGooglePayAvailability = async (
  isGooglePayInAndroidApp: boolean,
  site: Sites,
  currency?: Currency
): Promise<boolean> => {
  let isGooglePayAvailable = false;

  if (isGooglePayInAndroidApp) {
    await initGooglePayNativeClient();
    const googlePayServerSettings = await getGooglePaySystemDetails(site, currency);

    if (googlePayServerSettings) {
      const googlePayData = getGooglePayBaseConfigurationData(googlePayServerSettings);
      isGooglePayAvailable = await googlePayCheckAvailability(
        googlePayData,
        googlePayServerSettings.merchantPublicKey
      );
    }
  }

  return isGooglePayAvailable;
};
